import { useEffect, useRef, useCallback } from 'react';
import { TokenStorage } from '@/config/storage';
import { tokenRefreshManager } from '@/services/api';

interface UseTokenRefreshOptions {
  // Intervalo para verificar se o token precisa ser renovado (em ms)
  checkInterval?: number;
  // Tempo mínimo antes da expiração para renovar proativamente (em ms)
  proactiveRefreshThreshold?: number;
  // Se deve fazer refresh automático em background
  enableBackgroundRefresh?: boolean;
  // Callback chamado quando o token é renovado
  onTokenRefreshed?: (newToken: string) => void;
  // Callback chamado quando há erro na renovação
  onRefreshError?: (error: Error) => void;
  // Se o usuário está autenticado (opcional, para controle externo)
  isAuthenticated?: boolean;
}

export function useTokenRefresh(options: UseTokenRefreshOptions = {}) {
  const {
    checkInterval = 60000, // 1 minuto
    proactiveRefreshThreshold = 5 * 60 * 1000, // 5 minutos
    enableBackgroundRefresh = true,
    onTokenRefreshed,
    onRefreshError,
    isAuthenticated = true, // Por padrão assume que está autenticado se não informado
  } = options;

  const intervalRef = useRef<NodeJS.Timeout>();
  const lastCheckRef = useRef<number>(0);
  const isCheckingRef = useRef<boolean>(false);

  // Função para verificar se o usuário está realmente autenticado
  const isUserAuthenticated = useCallback(() => {
    // Verifica se há token e usuário no storage
    const token = TokenStorage.getAccessToken();
    const user = TokenStorage.getUser();
    
    // Se não há token ou usuário, não está autenticado
    if (!token || !user) {
      return false;
    }
    
    // Se o parâmetro isAuthenticated foi passado, usa ele
    if (options.isAuthenticated !== undefined) {
      return options.isAuthenticated;
    }
    
    // Verifica se o token não está completamente expirado
    return !tokenRefreshManager.isTokenFullyExpired(token);
  }, [options.isAuthenticated]);

  // Função para verificar se o token precisa ser renovado
  const checkAndRefreshToken = useCallback(async () => {
    // Evita múltiplas verificações simultâneas
    if (isCheckingRef.current) {
      console.log('[useTokenRefresh] Verificação já em andamento, pulando...');
      return;
    }

    try {
      isCheckingRef.current = true;
      
      // Verifica se o usuário está autenticado antes de tentar renovar
      if (!isUserAuthenticated()) {
        console.log('[useTokenRefresh] Usuário não autenticado, pulando renovação de token');
        return;
      }
      
      const token = TokenStorage.getAccessToken();
      if (!token) {
        console.log('[useTokenRefresh] Nenhum token encontrado, pulando renovação');
        return;
      }

      // Verifica se o token está próximo de expirar e se não está fazendo refresh
      if (tokenRefreshManager.isTokenExpiredOrNearExpiry(token) && !tokenRefreshManager.isRefreshingToken) {
        // Adiciona uma verificação adicional para evitar muitas requisições
        const timeSinceLastCheck = Date.now() - lastCheckRef.current;
        if (timeSinceLastCheck < 30000) { // Mínimo 30 segundos entre verificações
          console.log('[useTokenRefresh] Verificação muito recente, aguardando...');
          return;
        }
        
        console.log('[useTokenRefresh] Token próximo de expirar, renovando...');
        
        const newToken = await tokenRefreshManager.refreshAccessToken();
        
        if (onTokenRefreshed) {
          onTokenRefreshed(newToken);
        }
        
        console.log('[useTokenRefresh] Token renovado com sucesso');
      }
    } catch (error) {
      console.error('[useTokenRefresh] Erro ao renovar token:', error);
      
      // Trata erros específicos de forma diferente
      if (error instanceof Error) {
        if (error.message === 'Login em andamento') {
          console.log('[useTokenRefresh] Login em andamento, ignorando erro de refresh');
          return;
        }
        
        if (error.message === 'Refresh token muito frequente') {
          console.log('[useTokenRefresh] Refresh muito frequente, aguardando próxima tentativa');
          return;
        }
      }
      
      if (onRefreshError && error instanceof Error) {
        onRefreshError(error);
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [onTokenRefreshed, onRefreshError, isUserAuthenticated]);

  // Função para renovar token manualmente
  const refreshToken = useCallback(async () => {
    try {
      // Verifica se o usuário está autenticado antes de tentar renovar
      if (!isUserAuthenticated()) {
        throw new Error('Usuário não autenticado');
      }
      
      const newToken = await tokenRefreshManager.refreshAccessToken();
      
      if (onTokenRefreshed) {
        onTokenRefreshed(newToken);
      }
      
      return newToken;
    } catch (error) {
      console.error('[useTokenRefresh] Erro ao renovar token manualmente:', error);
      
      if (onRefreshError && error instanceof Error) {
        onRefreshError(error);
      }
      
      throw error;
    }
  }, [onTokenRefreshed, onRefreshError, isUserAuthenticated]);

  // Função para verificar se o token está válido
  const isTokenValid = useCallback(() => {
    const token = TokenStorage.getAccessToken();
    if (!token) return false;
    
    return !tokenRefreshManager.isTokenFullyExpired(token);
  }, []);

  // Função para verificar se o token está próximo de expirar
  const isTokenNearExpiry = useCallback(() => {
    const token = TokenStorage.getAccessToken();
    if (!token) return false;
    
    return tokenRefreshManager.isTokenExpiredOrNearExpiry(token);
  }, []);

  // Effect para configurar o refresh automático
  useEffect(() => {
    if (!enableBackgroundRefresh) {
      return;
    }

    // Verifica se o usuário está autenticado antes de configurar o intervalo
    if (!isUserAuthenticated()) {
      console.log('[useTokenRefresh] Usuário não autenticado, não configurando refresh automático');
      return;
    }

    // Adiciona um delay para evitar verificações imediatas na inicialização
    const initialDelay = setTimeout(() => {
      // Verifica se ainda está autenticado após o delay
      if (isUserAuthenticated()) {
        checkAndRefreshToken();
      }
    }, 2000); // 2 segundos de delay

    // Configura o intervalo para verificar periodicamente
    intervalRef.current = setInterval(() => {
      const now = Date.now();
      
      // Evita verificações muito frequentes
      if (now - lastCheckRef.current < checkInterval) {
        return;
      }
      
      lastCheckRef.current = now;
      checkAndRefreshToken();
    }, checkInterval);

    return () => {
      clearTimeout(initialDelay);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkAndRefreshToken, checkInterval, enableBackgroundRefresh, isUserAuthenticated]);

  // Effect para lidar com mudanças de visibilidade da página
  useEffect(() => {
    if (!enableBackgroundRefresh) {
      return;
    }

    // Verifica se o usuário está autenticado
    if (!isUserAuthenticated()) {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Quando a página volta a ficar visível, verifica o token
        // Adiciona um delay para evitar conflitos com outros eventos
        setTimeout(() => {
          checkAndRefreshToken();
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkAndRefreshToken, enableBackgroundRefresh, isUserAuthenticated]);

  // Effect para lidar com foco da janela
  useEffect(() => {
    if (!enableBackgroundRefresh) {
      return;
    }

    // Verifica se o usuário está autenticado
    if (!isUserAuthenticated()) {
      return;
    }

    const handleWindowFocus = () => {
      // Quando a janela ganha foco, verifica o token
      // Adiciona um delay para evitar conflitos com outros eventos
      setTimeout(() => {
        checkAndRefreshToken();
      }, 1000);
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [checkAndRefreshToken, enableBackgroundRefresh, isUserAuthenticated]);

  return {
    refreshToken,
    isTokenValid,
    isTokenNearExpiry,
    checkAndRefreshToken,
  };
} 